import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import checkIcon from '../../assets/icons/check.svg';
import xIcon from '../../assets/icons/x.svg';
import trashIcon from '../../assets/icons/trash.svg';
import blockIcon from '../../assets/icons/block.svg';
import check2Icon from '../../assets/icons/check2.svg';

const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  type = 'confirm', // 'confirm', 'delete', 'deactivate', 'cancel'
  title,
  message,
  messageComponent, // Custom message component for complex formatting
  confirmText,
  cancelText = 'Annuler',
  loading = false,
  confirmButtonSx = {}, // Custom styling for confirm button
  hasExtendedContent = false // New prop for delete-to-disable modals
}) => {
  // Define configurations for each type
  const typeConfig = {
    confirm: {
      icon: checkIcon,
      iconColor: '#0AD69F',
      buttonColor: '#0AD69F',
      hoverColor: '#08B894',
      defaultTitle: "Confirmer l'action",
      defaultMessage: "Êtes-vous sûr de vouloir effectuer cette action ?",
      defaultConfirmText: 'Confirmer'
    },
    delete: {
      icon: trashIcon,
      iconColor: '#EF456E',
      buttonColor: '#EF456E',
      hoverColor: '#D63A5F',
      defaultTitle: "Supprimer l'élément",
      defaultMessage: "Êtes-vous sûr de vouloir supprimer cet élément ? Cette action est irréversible.",
      defaultConfirmText: 'Supprimer'
    },
    deactivate: {
      icon: blockIcon,
      iconColor: '#EF456E',
      buttonColor: '#EF456E',
      hoverColor: '#D63A5F',
      defaultTitle: "Désactiver l'élément",
      defaultMessage: "Êtes-vous sûr de vouloir désactiver cet élément ?",
      defaultConfirmText: 'Désactiver'
    },
    activate: {
      icon: check2Icon,
      iconColor: '#0AD69F',
      buttonColor: '#0AD69F',
      hoverColor: '#08B894',
      defaultTitle: "Activer l'élément",
      defaultMessage: "Êtes-vous sûr de vouloir activer cet élément ?",
      defaultConfirmText: 'Activer'
    },
    cancel: {
      icon: xIcon,
      iconColor: '#EF456E',
      buttonColor: '#EF456E',
      hoverColor: '#D63A5F',
      defaultTitle: "Annuler l'action",
      defaultMessage: "Êtes-vous sûr de vouloir annuler cette action ?",
      defaultConfirmText: 'Annuler'
    }
  };

  const config = typeConfig[type] || typeConfig.confirm;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 1700 }}
      slotProps={{ root: { sx: { zIndex: 1700 } } }}
      PaperProps={{
        sx: {
          width: '600px',
          height: hasExtendedContent ? '530px' : '450px',
          backgroundColor: '#F9FAFC',
          boxShadow: '0px 20px 80px #00000011',
          borderRadius: '12px',
          opacity: 1,
          position: 'relative'
        }
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent
        sx={{
          padding: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 3,
          pt: 4, // Reduced top padding slightly
          pb: 4
        }}
      >
        {/* Icon Circle */}
        <Box
          sx={{
            width: '114px',
            height: '114px',
            backgroundColor: config.iconColor,
            borderRadius: '200px',
            opacity: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <img
            src={config.icon}
            alt={type}
            style={{
              width: '64px',
              height: '64px',
              filter: 'brightness(0) invert(1)', // Makes icon white
              strokeWidth: (type === 'cancel' || type === 'deactivate') ? '3px' : '2px' // Make X bolder
            }}
          />
        </Box>

        {/* Title */}
        <Typography
          sx={{
            textAlign: 'center',
            fontFamily: 'Montserrat',
            fontWeight: 600,
            fontSize: '20px',
            lineHeight: '32px',
            letterSpacing: '0px',
            opacity: 1,
            color: '#333333'
          }}
        >
          {title || config.defaultTitle}
        </Typography>

        {/* Message */}
        <Box
          sx={{
            textAlign: 'center',
            fontFamily: 'Montserrat',
            fontWeight: 600,
            fontSize: '20px',
            lineHeight: '32px',
            letterSpacing: '0px',
            opacity: 1,
            color: '#333333',
            maxWidth: '500px', // Increased from 366px to give more space
            minHeight: '56px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2 // Add horizontal padding to prevent text from touching edges
          }}
        >
          {messageComponent || (
            <Typography
              sx={{
                textAlign: 'center',
                fontFamily: 'Montserrat',
                fontWeight: 600,
                fontSize: '20px',
                lineHeight: '32px',
                letterSpacing: '0px',
                opacity: 1,
                color: '#333333'
              }}
            >
              {message || config.defaultMessage}
            </Typography>
          )}
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            marginTop: 2,
            alignItems: 'center' // Align button text vertically
          }}
        >
          {/* Cancel Button */}
          <Button
            onClick={onClose}
            disabled={loading}
            sx={{
              textAlign: 'left',
              fontFamily: 'Montserrat',
              fontWeight: 600,
              fontSize: '18px',
              lineHeight: '22px',
              letterSpacing: '0px',
              color: '#36495C',
                textTransform: 'none',
              opacity: 1,
              backgroundColor: 'transparent',
              boxShadow: 'none',
              padding: '13px 24px',
              minHeight: '48px', // Match confirm button height
              display: 'flex',
              alignItems: 'center', // Center text vertically
              '&:hover': {
                backgroundColor: 'rgba(54, 73, 92, 0.04)'
              }
            }}
          >
            {cancelText}
          </Button>

          {/* Action Button */}
          <Button
            onClick={onConfirm}
            disabled={loading}
            sx={{
              width: '193px',
              height: '48px',
              backgroundColor: config.buttonColor,
              boxShadow: '0px 4px 10px #BEBEBE50',
              borderRadius: '12px',
              opacity: 1,
              textAlign: 'left',
              fontFamily: 'Montserrat',
              fontWeight: 600,
              fontSize: '18px',
              lineHeight: '22px',
              letterSpacing: '0px',
              color: '#FFFFFF',
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              '&:hover': {
                backgroundColor: config.hoverColor
              },
              '&:disabled': {
                backgroundColor: '#CCCCCC',
                color: '#666666'
              },
              ...confirmButtonSx // Apply custom styling
            }}
          >
            {loading ? 'Chargement...' : (confirmText || config.defaultConfirmText)}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmationDialog;
